import { useEffect, useState } from "react";
import cn from "mcn";

import { useFeatureFlags } from "../../context/flags";
import { useEnableMfa, useMfaState, useOptOutOfMfa } from "../../data/mfa/hooks";
import { hideInitialLoader } from "../components/initial-loader";
import { CloseIcon } from "../icons/CloseIcon";
import { Button } from "../ui/button";
import { cardContainer } from "../ui/shared-styles";

/**
 * Proactively offers optional-mode MFA enrolment once per page load / login.
 * Renders nothing unless the feature flag is on and the user is in an `optional`
 * org with an undecided state when MFA state first settles. Mid-session changes
 * (e.g. disabling MFA in Settings) do not re-open this modal until refresh.
 */
export function MfaOfferGate() {
  const [mfaFlag] = useFeatureFlags("auth0-mfa-integration");
  const { offer, isLoading } = useMfaState();
  const enableMfa = useEnableMfa();
  const optOut = useOptOutOfMfa();
  const [dismissed, setDismissed] = useState(false);
  // Latch offer only on first settled load so disable → unknown does not re-open it.
  const [offerAtLoad, setOfferAtLoad] = useState<boolean | null>(null);

  useEffect(() => {
    if (isLoading || offerAtLoad !== null) return;
    setOfferAtLoad(offer);
  }, [isLoading, offer, offerAtLoad]);

  const visible = Boolean(mfaFlag) && offerAtLoad === true && !dismissed;

  // Hide the initial splash so the offer shows over the ready app, not the spinner.
  useEffect(() => {
    if (visible) hideInitialLoader();
  }, [visible]);

  if (!visible) return null;

  const remindLater = () => setDismissed(true);

  const optMeOut = () => {
    optOut.mutate();
    setDismissed(true);
  };

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50 px-3 py-6 overflow-y-auto"
      data-testid="mfa-offer-modal"
    >
      <div
        className={cn(
          cardContainer,
          "w-[calc(100vw-32px)] max-w-[640px] flex flex-col gap-4 rounded-[8px] select-none",
        )}
        role="dialog"
        aria-labelledby="mfa-offer-title"
        aria-modal="true"
      >
        <div className="flex items-center justify-between gap-6 px-6 pt-4">
          <div id="mfa-offer-title" className="text-lg font-semibold text-foreground break-words min-w-0">
            Secure your account with two-factor authentication
          </div>
          <CloseIcon onClick={remindLater} className="shrink-0" />
        </div>

        <div className="px-6 text-foreground text-[length:var(--font-size-base,12px)] leading-[var(--line-height-text-base,18px)]">
          Use your preferred authenticator app to add an extra layer of security to your account. You can change this
          later in Settings.
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-4 pt-1">
          <Button
            buttonType="linkButton"
            onClick={optMeOut}
            disabled={optOut.isPending}
            className="font-medium no-underline"
          >
            Opt out
          </Button>
          <div className="flex gap-4 ml-auto">
            <Button buttonType="cancel" onClick={remindLater} className="font-medium">
              Maybe later
            </Button>
            <Button buttonType="save" onClick={() => enableMfa()} className="font-medium">
              Set up now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
